import React, { useRef, useEffect } from 'react';

const Background = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    // === Configuration ===
    const starCount = 50; // Minimalist
    const baseColor = 'rgba(255, 255, 255, 0.2)'; // Very dim inactive stars
    const sparkColor = '255, 255, 255'; // Bright white active spark

    // "One at a Time" Controller State
    let stars = [];
    let activeStarIndex = -1; // Index of the star currently sparking (-1 = none)
    let sparkProgress = 0; // 0 to 100 (lifecycle of the spark)
    const sparkSpeed = 1.5; // How fast the spark animation plays

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initStars();
    };

    // Helper: Draws the "Cross" Lens Flare
    const drawSpark = (x, y, intensity) => {
      ctx.save();
      ctx.translate(x, y);

      // 1. The Glow Center
      ctx.shadowBlur = 15 * intensity;
      ctx.shadowColor = sparkColor;
      ctx.fillStyle = `rgba(${sparkColor}, ${intensity})`;
      ctx.beginPath();
      ctx.arc(0, 0, 1.5 * intensity, 0, Math.PI * 2);
      ctx.fill();

      // 2. The Cross Rays (Lens Flare)
      ctx.strokeStyle = `rgba(${sparkColor}, ${intensity})`;
      ctx.lineWidth = 0.5;
      ctx.shadowBlur = 0; // Sharp lines

      const rayLength = 12 * intensity; // Length grows with intensity

      ctx.beginPath();
      // Vertical Ray
      ctx.moveTo(0, -rayLength);
      ctx.lineTo(0, rayLength);
      // Horizontal Ray
      ctx.moveTo(-rayLength, 0);
      ctx.lineTo(rayLength, 0);
      ctx.stroke();

      ctx.restore();
    };

    class Star {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 1.5;
      }

      draw(isActive, intensity) {
        if (isActive) {
          drawSpark(this.x, this.y, intensity);
        } else {
          // Dormant State: Just a faint dot
          ctx.fillStyle = baseColor;
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    const initStars = () => {
      stars = [];
      for (let i = 0; i < starCount; i++) {
        stars.push(new Star());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // === Controller Logic ===
      // If no star is active, immediately pick one
      if (activeStarIndex === -1) {
        activeStarIndex = Math.floor(Math.random() * stars.length);
        sparkProgress = 0;
      } else {
        // Advance the current spark
        sparkProgress += sparkSpeed;
        if (sparkProgress >= 100) {
          // Immediately pick the next star (continuous sparkling)
          activeStarIndex = Math.floor(Math.random() * stars.length);
          sparkProgress = 0;
        }
      }

      // Calculate intensity (Bell curve: fades in 0-50, fades out 50-100)
      let intensity = 0;
      if (activeStarIndex !== -1) {
        if (sparkProgress < 50) {
          intensity = sparkProgress / 50; // 0 to 1
        } else {
          intensity = (100 - sparkProgress) / 50; // 1 to 0
        }
      }

      // === Draw Loop ===
      stars.forEach((star, index) => {
        star.draw(index === activeStarIndex, intensity);
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        background: '#09090b'
      }}
      className="fixed inset-0 z-0 pointer-events-none"
    />
  );
};

export default Background;